import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./MenuCategories.module.scss";
import BackdropArea from "./BackdropArea";
import banner from "@/assets/stub/banner.svg";
import { useServices } from "@/services-context";
import { Categories } from "@/services/categories";
import { B2cCategoryTreePayload } from "@/generated/graphql/b2c";
import { Gtag } from "@/services/gtag.ts";
import { useTranslation } from "react-i18next";
import { SubcategoriesBox } from "./SubcategoriesBox";
import { ItemButton } from "./ItemButton";
import { Popup } from "@/uikit";
import { getEnv } from "@/util/env.ts";

const getIconUrl = (path: string | null) => {
  if (!path?.length) return path;
  const {PUBLIC_B2C_ASSETS_HOST} = getEnv();
  return PUBLIC_B2C_ASSETS_HOST + "/" + path;
};

export interface MenuCategoriesProps {
  anchor: Element | null;
  onClose?: () => void;
}

export const MenuCategories: React.FC<MenuCategoriesProps> = ({ anchor, onClose }) => {
  const services = useServices();
  const { t } = useTranslation();
  const categories = services.categories as Categories;
  const [subcategory, setSubcategory] = useState<null | { key: string; target: Element }>(null);
  const [menuStructure, setMenuStructure] = useState(() => new MenuStructure());
  const outletRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMenuStructure((prev) => {
      const ms = new MenuStructure();
      ms.setTree(categories.findTopLevelCategories());
      return ms;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const handleCategoryClick = useCallback(
    (key: string, event: { detail: { target: Element }; type: string }) => {
      if (event.type === "click") {
        const category = categories.getItem(key);
        Gtag.pushEvent("category_click", category.name);
      }
      setSubcategory({ key, target: event.detail.target });
    },
    [categories],
  );

  const handleClose = useCallback(() => {
    if (onClose) onClose();
    // Можно пробросить наверх через пропсы
    const closeEvent = new CustomEvent("close", { composed: true, bubbles: true });
    // SSR-safe: не вызываем document/dispatchEvent вне useEffect
    if (typeof window !== "undefined") {
      window.dispatchEvent(closeEvent);
    }
  }, [onClose]);

  const handleCloseMenu = useCallback(() => {
    if (onClose) onClose();
    // Можно пробросить наверх через пропсы
    const closeEvent = new CustomEvent("close");
    if (typeof window !== "undefined") {
      window.dispatchEvent(closeEvent);
    }
  }, [onClose]);

  const renderSubcategories = useCallback(() => {
    if (!subcategory) return null;
    const rootId = subcategory.key;
    return (
      <div className={styles.subcategoriesPanel}>
        <div className={styles.subcategoriesBox}>
          <SubcategoriesBox menu={categories} rootId={rootId} onClose={handleClose} />
        </div>
        {/* TODO: Need logic to display banner? (At now bannerBox have `display: none` rule)
          <div className={styles.bannerBox}>
            <img src={banner} alt="banner" />
          </div>
        */}
      </div>
    );
  }, [subcategory, categories, handleClose]);

  const renderCategories = useMemo(() => {
    return (
      <div className={styles.categoriesBox}>
        {menuStructure.getRoots().map((item) => (
          <div className={classNames(styles.rootCategory)}>
            <ItemButton
              key={item.id}
              title={item.name}
              icon={item.icon}
              className={classNames('rootCategoryLink')}
              onHover={(event: any) => handleCategoryClick(item.key, event)}
              onClick={(event: any) => handleCategoryClick(item.key, event)}
            />
          </div>
        ))}
      </div>
    );
  }, [menuStructure, handleCategoryClick]);

  return (
    <>
      <BackdropArea onClick={handleCloseMenu} />
      <Popup anchor={anchor!} placement="bottom-start" active className={styles.popup} onClose={handleCloseMenu}>
        <div className={styles.box} ref={outletRef}>
          {renderCategories}
          {subcategory && renderSubcategories()}
        </div>
      </Popup>
    </>
  );
};

type MenuItem = {
  id: string;
  key: string;
  name: string;
  icon: string | null;
  level: number;
  url: string;
};

export class MenuStructure {
  items: Record<string, MenuItem> = {};
  links: Record<string, { parent: string[]; children: string[] }> = {};

  setTree(list: B2cCategoryTreePayload[]) {
    const queue = [...list] as B2cCategoryTreePayload[];
    while (queue.length) {
      const item = queue.shift()!;
      this.links[item.id] ??= { parent: [], children: [] };
      this.items[item.id] = this.toMenuItem(item);
      const parent = (this.links[item.id].parent ?? []).concat(item.id);
      for (const child of item.children) {
        this.links[item.id].children.push(child.id);
        this.links[child.id] = { parent: parent, children: [] };
        queue.push(child);
      }
    }
  }

  getItem(id: string) {
    return this.items[id];
  }

  getChilds(id: string) {
    return (this.links[id].children ?? []).map((id) => this.items[id]);
  }

  getParents(id: string) {
    return (this.links[id].parent ?? []).map((id) => this.items[id]);
  }

  getRoots() {
    return Object.values(this.items).filter((item) => this.links[item.id].parent.length === 0);
  }

  toMenuItem(value: B2cCategoryTreePayload): MenuItem {
    const parents = this.getParents(value.id);
    const ids = [...parents.map((item) => item.id), value.id];
    const path = ids.join("/");
    return {
      id: value.id,
      key: value.id,
      name: value.name,
      icon: getIconUrl(value.icon),
      level: parents.length,
      url: `en/categories/${path}`,
    };
  }
}
